import React, { useEffect, useMemo, useRef, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import multiMonthPlugin from "@fullcalendar/multimonth";
import interactionPlugin from "@fullcalendar/interaction";
import axios from "../api/axios";

const API_BASE = "/v2/activity-feed";
const getAuthHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("accessToken")}` });

const ROLE_OPTIONS = [
    { value: "manager", label: "Manager" },
    { value: "company", label: "Company" },
    { value: "technical_staff", label: "Technical Staff" },
    { value: "vendor", label: "Vendor" },
    { value: "auditor", label: "Auditor" },
    { value: "admin", label: "Admin" },
    { value: "contractor", label: "Contractor" },
    { value: "ai_system", label: "AI System" },
    { value: "system", label: "System" },
];

const MODULE_META = {
    appointment: { label: "Appointments", color: "#6366f1", icon: "📅" },
    ticket: { label: "Maintenance", color: "#f59e0b", icon: "🔧" },
    mastersheet: { label: "Mastersheet", color: "#0ea5e9", icon: "📋" },
    lease: { label: "Leases", color: "#8b5cf6", icon: "📄" },
    lead: { label: "Leads", color: "#0ea5e9", icon: "👤" },
    lead_call: { label: "AI Calls", color: "#0891b2", icon: "📞" },
    lead_chat: { label: "AI Chats", color: "#06b6d4", icon: "💬" },
    suite_inspection: { label: "Suite Inspections", color: "#0ea5e9", icon: "📝" },
    move_inspection: { label: "Move In/Out", color: "#06b6d4", icon: "🚚" },
    vendor_inspection: { label: "Vendor Inspections", color: "#f97316", icon: "🛡️" },
    building_inspection: { label: "Building Inspections", color: "#0d9488", icon: "🏢" },
    incident_report: { label: "Incident Reports", color: "#dc2626", icon: "⚠️" },
    outlook: { label: "Outlook", color: "#0078d4", icon: "☁️" },
};

const todayIso = () => new Date().toISOString().slice(0, 10);
const daysAgoIso = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);
const daysAheadIso = (n) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);

function ChipMultiSelect({ options, value, onChange, placeholder, single = false }) {
    return (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {options.map((opt) => {
                const active = value.includes(opt.value);
                return (
                    <button
                        key={opt.value}
                        type="button"
                        onClick={() => {
                            if (single) {
                                onChange(active ? [] : [opt.value]);
                            } else {
                                onChange(active ? value.filter((v) => v !== opt.value) : [...value, opt.value]);
                            }
                        }}
                        style={{
                            padding: "6px 10px",
                            border: `1px solid ${active ? opt.color || "#1e293b" : "#e2e8f0"}`,
                            background: active ? (opt.color ? `${opt.color}15` : "#f1f5f9") : "#fff",
                            color: active ? opt.color || "#1e293b" : "#475569",
                            borderRadius: 999,
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                        }}
                    >
                        {opt.icon ? <span>{opt.icon}</span> : null}
                        {opt.label}
                    </button>
                );
            })}
            {!options.length && <span style={{ color: "#94a3b8", fontSize: 12 }}>{placeholder || "None"}</span>}
        </div>
    );
}

function PeoplePicker({ selected, onChange }) {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const debounce = useRef(null);

    useEffect(() => {
        if (debounce.current) clearTimeout(debounce.current);
        debounce.current = setTimeout(async () => {
            setLoading(true);
            try {
                const r = await axios.get(`${API_BASE}/people`, { params: { search: query }, headers: getAuthHeader() });
                setResults(r.data?.data || []);
            } catch {
                setResults([]);
            } finally {
                setLoading(false);
            }
        }, 250);
        return () => debounce.current && clearTimeout(debounce.current);
    }, [query]);

    const toggle = (p) => {
        const exists = selected.find((s) => s.id === p.id);
        onChange(exists ? selected.filter((s) => s.id !== p.id) : [...selected, p]);
    };

    return (
        <div>
            <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search people…"
                style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #e2e8f0",
                    borderRadius: 8,
                    fontSize: 13,
                    outline: "none",
                }}
            />
            {selected.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                    {selected.map((p) => (
                        <span
                            key={p.id}
                            style={{
                                padding: "4px 8px",
                                background: "#1e293b",
                                color: "#fff",
                                borderRadius: 999,
                                fontSize: 11,
                                fontWeight: 600,
                                display: "inline-flex",
                                gap: 6,
                                alignItems: "center",
                            }}
                        >
                            {p.name}
                            <button
                                onClick={() => toggle(p)}
                                style={{
                                    border: "none",
                                    background: "transparent",
                                    color: "#fff",
                                    cursor: "pointer",
                                    fontSize: 14,
                                    lineHeight: 1,
                                    padding: 0,
                                }}
                            >
                                ×
                            </button>
                        </span>
                    ))}
                </div>
            )}
            <div style={{ marginTop: 8, maxHeight: 180, overflowY: "auto", border: "1px solid #f1f5f9", borderRadius: 8 }}>
                {loading && <div style={{ padding: 8, fontSize: 12, color: "#94a3b8" }}>Searching…</div>}
                {!loading && results.length === 0 && (
                    <div style={{ padding: 8, fontSize: 12, color: "#94a3b8" }}>No matches</div>
                )}
                {!loading &&
                    results.map((p) => {
                        const active = !!selected.find((s) => s.id === p.id);
                        return (
                            <button
                                key={p.id}
                                onClick={() => toggle(p)}
                                style={{
                                    display: "flex",
                                    width: "100%",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    padding: "8px 10px",
                                    border: "none",
                                    borderBottom: "1px solid #f8fafc",
                                    background: active ? "#eef2ff" : "#fff",
                                    cursor: "pointer",
                                    textAlign: "left",
                                }}
                            >
                                <div>
                                    <div style={{ fontSize: 13, color: "#1e293b", fontWeight: 600 }}>{p.name}</div>
                                    <div style={{ fontSize: 11, color: "#94a3b8" }}>
                                        {p.role} {p.email ? `· ${p.email}` : ""}
                                    </div>
                                </div>
                                {active && <span style={{ color: "#6366f1", fontSize: 12 }}>✓</span>}
                            </button>
                        );
                    })}
            </div>
        </div>
    );
}

function DetailSection({ title, children }) {
    return (
        <div>
            <div style={{ fontSize: 10, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>
                {title}
            </div>
            {children}
        </div>
    );
}

function KeyValueGrid({ entries }) {
    if (!entries.length) return null;
    return (
        <div
            style={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
                gap: "10px 16px",
                background: "#f8fafc",
                border: "1px solid #f1f5f9",
                borderRadius: 10,
                padding: "12px 14px",
            }}
        >
            {entries.map(([label, value], i) => (
                <div key={`${label}-${i}`} style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>
                        {label}
                    </div>
                    <div
                        style={{
                            fontSize: 13,
                            color: "#0f172a",
                            marginTop: 2,
                            fontWeight: 500,
                            wordBreak: "break-word",
                            lineHeight: 1.45,
                        }}
                    >
                        {value}
                    </div>
                </div>
            ))}
        </div>
    );
}

function renderDetailValue(value) {
    if (value == null) return "—";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (typeof value === "string" && /^https?:\/\//.test(value)) {
        return (
            <a href={value} target="_blank" rel="noopener noreferrer" style={{ color: "#0ea5e9" }}>
                Open link ↗
            </a>
        );
    }
    if (Array.isArray(value)) return value.map((v) => (typeof v === "object" ? JSON.stringify(v) : String(v))).join(", ");
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
}

function EventDetailModal({ event, onClose }) {
    if (!event) return null;
    const meta = MODULE_META[event.source_module] || { label: event.source_module, color: "#94a3b8", icon: "•" };

    const headerEntries = [
        ["When", new Date(event.timestamp).toLocaleString()],
        event.end_timestamp && ["Ends", new Date(event.end_timestamp).toLocaleString()],
        event.status && ["Status", String(event.status).replace(/_/g, " ")],
        event.prior_status && ["Previous", String(event.prior_status).replace(/_/g, " ")],
        event.event_type && ["Event", String(event.event_type).replace(/_/g, " ")],
        event.priority && ["Priority", event.priority],
    ].filter(Boolean);

    const whoWhereEntries = [
        event.actor?.name && [
            "Performed by",
            `${event.actor.name}${event.actor.role ? ` · ${String(event.actor.role).replace(/_/g, " ")}` : ""}`,
        ],
        event.actor?.email && ["Actor email", event.actor.email],
        event.property?.name && ["Property", event.property.name],
        event.property?.building && ["Building", event.property.building],
        event.property?.unit && ["Unit", event.property.unit],
        event.subject?.label && ["Reference", event.subject.label],
    ].filter(Boolean);

    const detailMap = event.metadata?.details && typeof event.metadata.details === "object" ? event.metadata.details : null;
    const detailEntries = detailMap
        ? Object.entries(detailMap)
            .filter(([, v]) => v !== undefined && v !== null && v !== "")
            .map(([k, v]) => [k, renderDetailValue(v)])
        : [];

    return (
        <div
            onClick={(e) => e.target === e.currentTarget && onClose()}
            style={{
                position: "fixed",
                inset: 0,
                background: "rgba(15,23,42,0.55)",
                backdropFilter: "blur(4px)",
                zIndex: 1000,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 20,
            }}
        >
            <div
                style={{
                    background: "#fff",
                    borderRadius: 18,
                    width: "100%",
                    maxWidth: 720,
                    maxHeight: "92vh",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    boxShadow: "0 25px 50px rgba(0,0,0,0.2)",
                }}
            >
                <div
                    style={{
                        padding: "18px 22px",
                        borderBottom: "1px solid #f1f5f9",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        background: `linear-gradient(135deg, ${meta.color}15 0%, ${meta.color}05 100%)`,
                    }}
                >
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 12, minWidth: 0 }}>
                        <span
                            style={{
                                width: 36,
                                height: 36,
                                borderRadius: 10,
                                background: meta.color,
                                color: "#fff",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 18,
                                flexShrink: 0,
                            }}
                        >
                            {meta.icon}
                        </span>
                        <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 11, fontWeight: 800, color: meta.color, textTransform: "uppercase", letterSpacing: 0.8 }}>
                                {meta.label}
                            </div>
                            <div style={{ fontSize: 17, fontWeight: 700, color: "#0f172a", marginTop: 2, lineHeight: 1.3 }}>{event.title}</div>
                            {event.description && (
                                <div style={{ fontSize: 13, color: "#475569", marginTop: 6, lineHeight: 1.55 }}>{event.description}</div>
                            )}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            border: "none",
                            background: "transparent",
                            fontSize: 26,
                            color: "#94a3b8",
                            cursor: "pointer",
                            lineHeight: 1,
                            marginLeft: 12,
                            flexShrink: 0,
                        }}
                    >
                        ×
                    </button>
                </div>

                <div
                    style={{
                        flex: 1,
                        overflowY: "auto",
                        padding: "18px 22px",
                        display: "flex",
                        flexDirection: "column",
                        gap: 18,
                    }}
                >
                    <DetailSection title="Timing & status">
                        <KeyValueGrid entries={headerEntries} />
                    </DetailSection>

                    {whoWhereEntries.length > 0 && (
                        <DetailSection title="Who & where">
                            <KeyValueGrid entries={whoWhereEntries} />
                        </DetailSection>
                    )}

                    {detailEntries.length > 0 && (
                        <DetailSection title={`${meta.label} details`}>
                            <KeyValueGrid entries={detailEntries} />
                        </DetailSection>
                    )}

                    {event.metadata?.raw && (
                        <details style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 12px" }}>
                            <summary style={{ cursor: "pointer", fontSize: 12, color: "#64748b", fontWeight: 700 }}>
                                Raw source data
                            </summary>
                            <pre
                                style={{
                                    margin: "10px 0 0",
                                    fontSize: 11,
                                    color: "#475569",
                                    overflow: "auto",
                                    maxHeight: 280,
                                    background: "#fff",
                                    padding: 10,
                                    borderRadius: 6,
                                    border: "1px solid #e2e8f0",
                                }}
                            >
                                {JSON.stringify(event.metadata.raw, null, 2)}
                            </pre>
                        </details>
                    )}
                </div>

                <div
                    style={{
                        padding: "14px 22px",
                        borderTop: "1px solid #f1f5f9",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        background: "#fafbfc",
                    }}
                >
                    {event.link ? (
                        <a
                            href={event.link}
                            target={event.link.startsWith("http") ? "_blank" : undefined}
                            rel="noopener noreferrer"
                            style={{
                                fontSize: 13,
                                padding: "9px 16px",
                                background: meta.color,
                                color: "#fff",
                                borderRadius: 8,
                                textDecoration: "none",
                                fontWeight: 700,
                            }}
                        >
                            Open in source →
                        </a>
                    ) : (
                        <span />
                    )}
                    <button
                        onClick={onClose}
                        style={{
                            fontSize: 13,
                            padding: "9px 16px",
                            background: "#fff",
                            color: "#1e293b",
                            border: "1px solid #e2e8f0",
                            borderRadius: 8,
                            fontWeight: 600,
                            cursor: "pointer",
                        }}
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function ActivityCalendar() {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [counts, setCounts] = useState({});
    const [from, setFrom] = useState(daysAgoIso(30));
    const [to, setTo] = useState(daysAheadIso(60));
    const [modules, setModules] = useState([]);
    const [roles, setRoles] = useState([]);
    const [people, setPeople] = useState([]);
    const [search, setSearch] = useState("");
    const [systemFilter, setSystemFilter] = useState("");
    const [selected, setSelected] = useState(null);

    const moduleOptions = Object.entries(MODULE_META).map(([value, m]) => ({
        value,
        label: m.label,
        color: m.color,
        icon: m.icon,
    }));

    const fetchEvents = async () => {
        setLoading(true);
        try {
            const params = {
                from: new Date(from).toISOString(),
                to: new Date(`${to}T23:59:59`).toISOString(),
            };
            if (modules.length) params.modules = modules.join(",");
            if (roles.length) params.roles = roles.join(",");
            if (people.length) {
                params.user_ids = people.map((p) => p.id).join(",");
                params.user_names = people.map((p) => p.name).filter(Boolean).join(",");
            }
            if (search.trim()) params.search = search.trim();
            if (systemFilter) params.system_generated = systemFilter;

            const r = await axios.get(API_BASE, { params, headers: getAuthHeader() });
            setEvents(r.data?.data || []);
            setCounts(r.data?.meta?.counts || {});
        } catch (e) {
            console.error(e);
            setEvents([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEvents();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [from, to, modules, roles, people, systemFilter]);

    const calendarEvents = useMemo(
        () =>
            events.map((e) => ({
                id: e.id,
                title: e.title,
                start: e.timestamp,
                end: e.end_timestamp,
                allDay: !!e.all_day,
                backgroundColor: e.color || "#6b7280",
                borderColor: e.color || "#6b7280",
                textColor: "#ffffff",
                extendedProps: e,
            })),
        [events]
    );

    return (
        <div style={{ padding: 24, background: "#f8fafc", minHeight: "100vh" }}>
            <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12 }}>
                <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.8 }}>
                        Operational Timeline
                    </div>
                    <h1 style={{ fontSize: 28, fontWeight: 800, color: "#0f172a", margin: "4px 0 0" }}>Activity Calendar</h1>
                    <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
                        Unified view of every operational event across RentDigiCare.
                    </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 12, color: "#64748b" }}>
                    {loading ? "Loading…" : `${events.length} events`}
                </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: 16 }}>
                {/* Filter rail */}
                <aside
                    style={{
                        background: "#fff",
                        border: "1px solid #e2e8f0",
                        borderRadius: 14,
                        padding: 16,
                        display: "flex",
                        flexDirection: "column",
                        gap: 16,
                        height: "fit-content",
                        position: "sticky",
                        top: 16,
                    }}
                >
                    <div>
                        <label style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.6 }}>Date range</label>
                        <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                            <input
                                type="date"
                                value={from}
                                onChange={(e) => setFrom(e.target.value)}
                                style={{ flex: 1, padding: "6px 8px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12 }}
                            />
                            <input
                                type="date"
                                value={to}
                                onChange={(e) => setTo(e.target.value)}
                                style={{ flex: 1, padding: "6px 8px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12 }}
                            />
                        </div>
                        <div style={{ display: "flex", gap: 4, marginTop: 6, flexWrap: "wrap" }}>
                            {[
                                ["Today", todayIso(), todayIso()],
                                ["7d", daysAgoIso(7), todayIso()],
                                ["30d", daysAgoIso(30), todayIso()],
                                ["Upcoming", todayIso(), daysAheadIso(60)],
                            ].map(([label, f, t]) => (
                                <button
                                    key={label}
                                    onClick={() => {
                                        setFrom(f);
                                        setTo(t);
                                    }}
                                    style={{
                                        flex: 1,
                                        padding: "4px 8px",
                                        background: "#f1f5f9",
                                        border: "none",
                                        borderRadius: 6,
                                        fontSize: 11,
                                        fontWeight: 600,
                                        color: "#475569",
                                        cursor: "pointer",
                                    }}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.6 }}>Search</label>
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && fetchEvents()}
                            placeholder="Title, name, unit…"
                            style={{
                                width: "100%",
                                padding: "8px 12px",
                                border: "1px solid #e2e8f0",
                                borderRadius: 8,
                                fontSize: 13,
                                marginTop: 6,
                                outline: "none",
                            }}
                        />
                    </div>

                    <div>
                        <label style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.6 }}>Modules</label>
                        <div style={{ marginTop: 6 }}>
                            <ChipMultiSelect options={moduleOptions} value={modules} onChange={setModules} single />
                        </div>
                    </div>

                    <div>
                        <label style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.6 }}>Roles</label>
                        <div style={{ marginTop: 6 }}>
                            <ChipMultiSelect options={ROLE_OPTIONS} value={roles} onChange={setRoles} single />
                        </div>
                    </div>

                    <div>
                        <label style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.6 }}>People</label>
                        <div style={{ marginTop: 6 }}>
                            <PeoplePicker selected={people} onChange={setPeople} />
                        </div>
                    </div>

                    <div>
                        <label style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.6 }}>Source</label>
                        <div style={{ display: "flex", gap: 4, marginTop: 6 }}>
                            {[
                                ["", "All"],
                                ["false", "Human"],
                                ["true", "System"],
                            ].map(([v, l]) => (
                                <button
                                    key={l}
                                    onClick={() => setSystemFilter(v)}
                                    style={{
                                        flex: 1,
                                        padding: "6px 8px",
                                        background: systemFilter === v ? "#1e293b" : "#f1f5f9",
                                        color: systemFilter === v ? "#fff" : "#475569",
                                        border: "none",
                                        borderRadius: 6,
                                        fontSize: 11,
                                        fontWeight: 600,
                                        cursor: "pointer",
                                    }}
                                >
                                    {l}
                                </button>
                            ))}
                        </div>
                    </div>

                    <button
                        onClick={() => {
                            setModules([]);
                            setRoles([]);
                            setPeople([]);
                            setSearch("");
                            setSystemFilter("");
                        }}
                        style={{
                            padding: "8px 12px",
                            background: "transparent",
                            border: "1px solid #e2e8f0",
                            borderRadius: 8,
                            fontSize: 12,
                            color: "#64748b",
                            fontWeight: 600,
                            cursor: "pointer",
                        }}
                    >
                        Clear filters
                    </button>
                </aside>

                {/* Main calendar */}
                <main style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: 16 }}>
                    {Object.keys(counts).length > 0 && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
                            {Object.entries(counts).map(([mod, count]) => {
                                const m = MODULE_META[mod] || { label: mod, color: "#94a3b8", icon: "•" };
                                return (
                                    <span
                                        key={mod}
                                        style={{
                                            padding: "4px 10px",
                                            background: `${m.color}15`,
                                            color: m.color,
                                            borderRadius: 999,
                                            fontSize: 11,
                                            fontWeight: 700,
                                            display: "inline-flex",
                                            gap: 6,
                                            alignItems: "center",
                                        }}
                                    >
                                        <span>{m.icon}</span> {m.label} · {count}
                                    </span>
                                );
                            })}
                        </div>
                    )}

                    <div className="activity-calendar-wrap">
                        <FullCalendar
                            plugins={[dayGridPlugin, timeGridPlugin, multiMonthPlugin, interactionPlugin]}
                            initialView="dayGridMonth"
                            headerToolbar={{
                                left: "prev,next today",
                                center: "title",
                                right: "multiMonthYear,dayGridMonth,timeGridWeek,timeGridDay",
                            }}
                            events={calendarEvents}
                            height="auto"
                            eventClick={(info) => {
                                info.jsEvent.preventDefault();
                                setSelected(info.event.extendedProps);
                            }}
                            eventDisplay="block"
                            displayEventTime={false}
                            dayMaxEvents={3}
                            nowIndicator
                            eventContent={(arg) => {
                                const p = arg.event.extendedProps || {};
                                const m = MODULE_META[p.source_module] || {};
                                const icon = m.icon || "•";
                                const isMonth = arg.view.type === "dayGridMonth" || arg.view.type === "multiMonthYear";
                                if (isMonth) {
                                    return {
                                        html: `<div class="ac-event ac-event--month" style="--ac-color:${arg.event.backgroundColor}">
                                            <span class="ac-event__icon">${icon}</span>
                                            <span class="ac-event__title">${escapeHtml(arg.event.title)}</span>
                                        </div>`,
                                    };
                                }
                                return {
                                    html: `<div class="ac-event ac-event--time" style="--ac-color:${arg.event.backgroundColor}">
                                        <div class="ac-event__title-row"><span>${icon}</span><strong>${escapeHtml(arg.event.title)}</strong></div>
                                        ${p.description ? `<div class="ac-event__desc">${escapeHtml(p.description).slice(0, 120)}</div>` : ""}
                                    </div>`,
                                };
                            }}
                            eventDidMount={(info) => {
                                const p = info.event.extendedProps || {};
                                const parts = [
                                    info.event.title,
                                    p.actor?.name ? `by ${p.actor.name}` : "",
                                    p.property?.name
                                        ? `${p.property.name}${p.property.unit ? ` · Unit ${p.property.unit}` : ""}`
                                        : "",
                                    p.status ? `Status: ${p.status}` : "",
                                ].filter(Boolean);
                                info.el.setAttribute("title", parts.join("\n"));
                            }}
                        />
                    </div>
                </main>
            </div>

            <EventDetailModal event={selected} onClose={() => setSelected(null)} />
            <style>{ACTIVITY_CALENDAR_CSS}</style>
        </div>
    );
}

function escapeHtml(s) {
    if (s == null) return "";
    return String(s)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

const ACTIVITY_CALENDAR_CSS = `
.activity-calendar-wrap .fc {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    font-size: 13px;
}
.activity-calendar-wrap .fc .fc-toolbar-title {
    font-size: 18px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.01em;
}
.activity-calendar-wrap .fc .fc-button {
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    color: #475569;
    text-transform: capitalize;
    font-weight: 600;
    font-size: 12px;
    box-shadow: none;
    padding: 6px 12px;
    border-radius: 8px;
}
.activity-calendar-wrap .fc .fc-button:hover {
    background: #e2e8f0;
    color: #1e293b;
}
.activity-calendar-wrap .fc .fc-button-primary:not(:disabled).fc-button-active,
.activity-calendar-wrap .fc .fc-button-primary:not(:disabled):active {
    background: #1e293b;
    border-color: #1e293b;
    color: #fff;
}
.activity-calendar-wrap .fc .fc-button-group .fc-button {
    border-radius: 0;
}
.activity-calendar-wrap .fc .fc-button-group .fc-button:first-child { border-top-left-radius: 8px; border-bottom-left-radius: 8px; }
.activity-calendar-wrap .fc .fc-button-group .fc-button:last-child { border-top-right-radius: 8px; border-bottom-right-radius: 8px; }
.activity-calendar-wrap .fc .fc-col-header-cell-cushion {
    font-size: 11px;
    font-weight: 700;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    padding: 10px 6px;
}
.activity-calendar-wrap .fc .fc-daygrid-day-number {
    font-size: 12px;
    color: #475569;
    font-weight: 600;
    padding: 6px 8px;
}
.activity-calendar-wrap .fc .fc-day-today {
    background: #fef3c7 !important;
}
.activity-calendar-wrap .fc .fc-day-today .fc-daygrid-day-number {
    color: #92400e;
    font-weight: 800;
}
.activity-calendar-wrap .fc-theme-standard td,
.activity-calendar-wrap .fc-theme-standard th {
    border-color: #f1f5f9;
}
.activity-calendar-wrap .fc-daygrid-event {
    border: none !important;
    background: transparent !important;
    padding: 0 !important;
    margin: 1px 4px !important;
}
.activity-calendar-wrap .fc-daygrid-event-harness { margin-top: 1px; }
.activity-calendar-wrap .fc-h-event { background: transparent !important; border: none !important; }
.activity-calendar-wrap .fc-daygrid-event-dot { display: none; }
.activity-calendar-wrap .fc-event-time { display: none; }

.activity-calendar-wrap .ac-event {
    cursor: pointer;
    transition: transform 0.08s ease, box-shadow 0.08s ease;
    font-size: 11px;
    line-height: 1.3;
    overflow: hidden;
}
.activity-calendar-wrap .ac-event:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.12);
}
.activity-calendar-wrap .ac-event--month {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 3px 9px;
    background: var(--ac-color);
    color: #fff;
    border-radius: 999px;
    font-weight: 600;
    white-space: nowrap;
    text-overflow: ellipsis;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
}
.activity-calendar-wrap .ac-event__icon {
    flex-shrink: 0;
    font-size: 11px;
    line-height: 1;
}
.activity-calendar-wrap .ac-event__title {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.activity-calendar-wrap .ac-event--time {
    padding: 5px 8px;
    background: var(--ac-color);
    color: #fff;
    border-radius: 6px;
    border-left: 4px solid color-mix(in srgb, var(--ac-color) 70%, black);
}
.activity-calendar-wrap .ac-event--time .ac-event__title-row {
    display: flex;
    align-items: center;
    gap: 6px;
    font-weight: 700;
}
.activity-calendar-wrap .ac-event--time .ac-event__desc {
    opacity: 0.9;
    font-size: 10px;
    margin-top: 2px;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
}
.activity-calendar-wrap .fc-daygrid-more-link {
    font-size: 11px;
    color: #6366f1;
    font-weight: 700;
    padding: 2px 6px;
    background: #eef2ff;
    border-radius: 4px;
    text-decoration: none;
}
.activity-calendar-wrap .fc-daygrid-more-link:hover {
    background: #e0e7ff;
}
.activity-calendar-wrap .fc-timegrid-slot {
    height: 2.5em;
}
.activity-calendar-wrap .fc-popover {
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    box-shadow: 0 12px 30px rgba(15, 23, 42, 0.15);
    overflow: hidden;
}
.activity-calendar-wrap .fc-popover-header {
    background: #f8fafc;
    padding: 10px 14px;
    font-weight: 700;
    color: #1e293b;
    font-size: 12px;
}
.activity-calendar-wrap .fc-popover-body {
    padding: 8px;
    max-height: 320px;
    overflow-y: auto;
}
`;
